import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { customerApi } from '@vmgate/common';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { ErrorState } from '../components/common/ErrorState';
import { StatusBadge } from '../components/common/StatusBadge';
import { formatDateTime } from '../utils/format';

const STATUS_FILTERS = ['ALL', 'ACTIVE', 'SUSPENDED', 'REVOKED'];

export function LicensesPage() {
  const { account } = useAuth();
  const [status, setStatus] = useState<string>('ALL');

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['customer-licenses', account?.customer_id],
    queryFn: () => customerApi.getCustomerLicenses(account!.customer_id),
    enabled: Boolean(account?.customer_id)
  });

  if (!account || isLoading) {
    return <LoadingScreen message="Loading licenses..." />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load licenses." onRetry={() => refetch()} />;
  }

  const filteredLicenses =
    status === 'ALL' ? data.licenses : data.licenses.filter((license) => license.status === status);

  return (
    <div className="surface" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
        <div>
          <h2 style={{ margin: 0 }}>Licenses</h2>
          <p className="text-muted" style={{ margin: 0 }}>
            View the certificates issued for your deployment.
          </p>
        </div>
        <select value={status} onChange={(event) => setStatus(event.target.value)} style={selectStyle} disabled={isFetching}>
          {STATUS_FILTERS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </header>

      <div className="text-muted">
        {data.license_quota_assigned}/{data.license_quota_total} active · {data.license_quota_available} available
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>License ID</th>
              <th>Status</th>
              <th>Issued at</th>
              <th>Device</th>
              <th>Certificate email</th>
            </tr>
          </thead>
          <tbody>
            {filteredLicenses.map((license) => (
              <tr key={license.license_id}>
                <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{license.license_id}</td>
                <td>
                  <StatusBadge
                    label={license.status}
                    tone={license.status === 'ACTIVE' ? 'success' : license.status === 'REVOKED' ? 'danger' : 'info'}
                  />
                </td>
                <td>{formatDateTime(license.created_at)}</td>
                <td>
                  {license.device_info ? (
                    <>
                      <div>{license.device_info.build_brand ?? 'Device'}</div>
                      <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {license.device_info.build_model ?? ''}
                      </div>
                    </>
                  ) : (
                    '—'
                  )}
                </td>
                <td>{license.certificate_email || '—'}</td>
              </tr>
            ))}
            {filteredLicenses.length === 0 && (
              <tr>
                <td colSpan={5} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                  No licenses match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  border: '1px solid var(--color-border)',
  borderRadius: '999px',
  padding: '0.5rem 0.85rem',
  fontSize: '0.9rem',
  background: '#fff'
};

